import type { MapViewport } from '../components/MapView';

/**
 * Keeps the map position in the URL so a view can be linked and reloaded.
 *
 * This deliberately bypasses the router and writes history directly: the map
 * emits a position on every pan and zoom, and routing each of those through
 * React state would re-render the tree during a drag. `replaceState` updates
 * the address bar without a render or a history entry.
 */

/** Opens over the continental US, which is where radar coverage is densest. */
export const DEFAULT_VIEWPORT: MapViewport = {
  longitude: -95.7,
  latitude: 38.5,
  zoom: 4,
};

/** Coordinates are stored to ~10m; more precision is noise in a URL. */
const PRECISION = 4;

export function readViewportFromUrl(search: string): MapViewport {
  const params = new URLSearchParams(search);

  const longitude = toNumber(params.get('lon'), -180, 180);
  const latitude = toNumber(params.get('lat'), -85, 85);
  const zoom = toNumber(params.get('z'), 0, 14);

  if (longitude === null || latitude === null) return DEFAULT_VIEWPORT;

  return { longitude, latitude, zoom: zoom ?? DEFAULT_VIEWPORT.zoom };
}

/**
 * Whether the URL carries an explicit position, as opposed to falling back to
 * the default view.
 *
 * This is what separates "someone opened the app" from "someone followed a
 * link to a place". Auto-locating on load is helpful in the first case and
 * hostile in the second — it would throw the viewer straight off the map they
 * were sent to look at.
 */
export function hasViewportInUrl(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('lon') && params.has('lat');
}

export function writeViewportToUrl(viewport: MapViewport): void {
  const params = new URLSearchParams(window.location.search);
  params.set('lon', viewport.longitude.toFixed(PRECISION));
  params.set('lat', viewport.latitude.toFixed(PRECISION));
  params.set('z', viewport.zoom.toFixed(1));

  window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
}

function toNumber(raw: string | null, min: number, max: number): number | null {
  if (raw === null) return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < min || value > max) return null;
  return value;
}
